import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  ConvertToSoResponse,
  LineContext,
  POReceivedWrite,
  Quote,
  QuoteRejectWrite,
  QuoteStatus,
  QuoteTransitionWrite,
  QuoteWrite,
  QuotesResponse,
  SendPreview,
  SendQuoteResponse,
} from './types'

const ROOT = '/api/quotes'

export interface UseQuotesOptions {
  q?: string
  status?: QuoteStatus | null
  customerId?: number | null
  submittedBy?: string | null
  includeArchived?: boolean
}

export function useQuotes(opts: UseQuotesOptions = {}) {
  const q = (opts.q ?? '').trim()
  const status = opts.status ?? null
  const customerId = opts.customerId ?? null
  const submittedBy = opts.submittedBy ?? null
  const includeArchived = Boolean(opts.includeArchived)

  return useQuery({
    queryKey: [
      'quotes', 'list', q, status, customerId, submittedBy, includeArchived,
    ],
    queryFn: () => {
      const search = new URLSearchParams()
      if (q) search.set('q', q)
      if (status) search.set('status', status)
      if (customerId !== null) search.set('customer_id', String(customerId))
      if (submittedBy) search.set('submitted_by', submittedBy)
      if (includeArchived) search.set('include_archived', '1')
      const qs = search.toString()
      return jsonFetch<QuotesResponse>(qs ? `${ROOT}?${qs}` : ROOT)
    },
    staleTime: 15_000,
  })
}

export function useQuote(id: number | null) {
  return useQuery({
    queryKey: ['quotes', 'detail', id],
    queryFn: () => jsonFetch<Quote>(`${ROOT}/${id}`),
    enabled: id !== null,
    staleTime: 15_000,
  })
}

export function useCreateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: QuoteWrite) =>
      jsonFetch<Quote>(ROOT, { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
    },
  })
}

export interface UpdateQuoteVars {
  id: number
  body: QuoteWrite
}

export function useUpdateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: UpdateQuoteVars) =>
      jsonFetch<Quote>(`${ROOT}/${vars.id}`, {
        method: 'PUT',
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', vars.id] })
    },
  })
}

export function useDeleteQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<{ ok: true; deleted_id: number; archived_at: string }>(
        `${ROOT}/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
    },
  })
}

export function useRestoreQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<Quote>(`${ROOT}/${id}/restore`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', id] })
    },
  })
}

// ─── Workflow mutations ─────────────────────────────────────────────

export interface TransitionQuoteVars {
  id: number
  body: QuoteTransitionWrite
}

export function useTransitionQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: TransitionQuoteVars) =>
      jsonFetch<Quote>(`${ROOT}/${vars.id}/transition`, {
        method: 'POST',
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', vars.id] })
    },
  })
}

export function useSubmitQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<Quote>(`${ROOT}/${id}/submit`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', id] })
    },
  })
}

export interface CustomerRejectVars {
  id: number
  body: QuoteRejectWrite
}

/** Sprint 12c — sales records that the customer rejected the quote.
 *  Transitions sent → customer_rejected. Same payload shape as
 *  /reject (reason required + optional comment). */
export function useCustomerReject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: CustomerRejectVars) =>
      jsonFetch<Quote>(`${ROOT}/${vars.id}/customer-reject`, {
        method: 'POST',
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', vars.id] })
    },
  })
}

export function useApproveQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<Quote>(`${ROOT}/${id}/approve`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', id] })
    },
  })
}

export interface RejectQuoteVars {
  id: number
  body: QuoteRejectWrite
}

export function useRejectQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: RejectQuoteVars) =>
      jsonFetch<Quote>(`${ROOT}/${vars.id}/reject`, {
        method: 'POST',
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', vars.id] })
    },
  })
}

// ─── Send + PO + convert-to-SO (Chunks 6–7) ─────────────────────────

export interface SendQuoteVars {
  id: number
  override_recipient?: string | null
  cc?: string[]
  bcc?: string[]
  subject?: string
  body?: string
}

export function useSendQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: SendQuoteVars) =>
      jsonFetch<SendQuoteResponse>(`${ROOT}/${vars.id}/send`, {
        method: 'POST',
        body: {
          override_recipient: vars.override_recipient ?? null,
          cc: vars.cc ?? null,
          bcc: vars.bcc ?? null,
          subject: vars.subject ?? null,
          body: vars.body ?? null,
        },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', vars.id] })
    },
  })
}

/** Fetches the canonical pre-fills for the Send Quote dialog —
 *  resolved recipient, default subject, default body. Disabled until
 *  caller passes a real id (typically when the dialog opens). */
export function useSendPreview(id: number | null) {
  return useQuery({
    queryKey: ['quotes', 'send-preview', id],
    queryFn: () => jsonFetch<SendPreview>(`${ROOT}/${id}/send-preview`),
    enabled: id !== null,
    staleTime: 30_000,
  })
}

export interface POReceivedVars {
  id: number
  body: POReceivedWrite
}

export function usePoReceived() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: POReceivedVars) =>
      jsonFetch<{ ok: true; quote: Quote }>(
        `${ROOT}/${vars.id}/po-received`,
        { method: 'POST', body: vars.body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', vars.id] })
    },
  })
}

export function useConvertToSo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<ConvertToSoResponse>(`${ROOT}/${id}/convert-to-so`, {
        method: 'POST',
      }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      void qc.invalidateQueries({ queryKey: ['quotes', 'detail', id] })
    },
  })
}

// ─── Line context (auto-fill on product pick) ───────────────────────

export interface UseLineContextOptions {
  productId: number | null
  customerId: number | null
}

/** Fetches UoM, packagings, customer pricelist match, last-paid, and
 *  best vendor cost for a given product (and optional customer) so
 *  the quote line editor can auto-fill defaults. Disabled until
 *  productId is set; staleTime 60s (vendor + pricelist data changes
 *  slowly). */
export function useLineContext(opts: UseLineContextOptions) {
  const { productId, customerId } = opts
  return useQuery({
    queryKey: ['quotes', 'line-context', productId, customerId],
    queryFn: () => {
      const search = new URLSearchParams()
      search.set('product_id', String(productId))
      if (customerId !== null) search.set('customer_id', String(customerId))
      return jsonFetch<LineContext>(`${ROOT}/line-context?${search}`)
    },
    enabled: productId !== null,
    staleTime: 60_000,
  })
}

// ─── PDF render (Chunk 5) ───────────────────────────────────────────

export function useRenderQuotePdf() {
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<{ file_id: string; download_url: string }>(
        `${ROOT}/${id}/pdf`,
        { method: 'POST' },
      ),
  })
}
